import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useAPI } from 'renderer/lib/authContext';

dayjs.extend(utc);

interface PoolWaitingJob {
  job_id: string;
  experiment_id: string;
  task_name: string;
  user_email: string;
  gpus: number;
  position: number;
  waiting_since?: string | null;
  message: string;
}

interface CapacityPoolUsage {
  accelerator: string;
  max_gpus: number;
  // Null when the server could not count running jobs in time. Unknown, never 0.
  in_use: number | null;
  available: number | null;
  waiting: PoolWaitingJob[];
}

interface ProviderPoolsReport {
  provider_id: string;
  provider_name: string;
  starvation_minutes: number;
  usage_known: boolean;
  pools: CapacityPoolUsage[];
}

interface ProviderPoolsModalProps {
  open: boolean;
  onClose: () => void;
  providerId: string;
  providerName?: string;
}

function usageLine(pool: CapacityPoolUsage): string {
  if (pool.in_use === null) {
    return `${pool.max_gpus} GPUs reserved, usage unknown`;
  }
  return `${pool.in_use} of ${pool.max_gpus} in use, ${pool.available ?? 0} free`;
}

function PoolSection({ pool }: { pool: CapacityPoolUsage }) {
  const percent =
    pool.in_use === null || pool.max_gpus < 1
      ? null
      : Math.min(100, (pool.in_use / pool.max_gpus) * 100);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography level="title-md">
          <code>{pool.accelerator}</code>
        </Typography>
        <Typography level="body-sm">{usageLine(pool)}</Typography>
      </Stack>
      {percent !== null && (
        <LinearProgress
          determinate
          value={percent}
          color={pool.available === 0 ? 'warning' : 'primary'}
          sx={{ my: 1, height: 6, borderRadius: 'sm' }}
        />
      )}
      {pool.waiting.length === 0 ? (
        <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 0.5 }}>
          Nobody waiting.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="sm" stripe="odd" sx={{ mt: 0.5 }}>
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Job</th>
                <th>User</th>
                <th style={{ width: 56, textAlign: 'right' }}>GPUs</th>
                <th style={{ width: 120 }}>Waiting since</th>
              </tr>
            </thead>
            <tbody>
              {pool.waiting.map((w) => (
                <tr key={w.job_id} title={w.message}>
                  <td>{w.position}</td>
                  <td>
                    {w.task_name || '—'}
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      {w.job_id}
                    </Typography>
                  </td>
                  <td>{w.user_email || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{w.gpus}</td>
                  <td>
                    {w.waiting_since
                      ? dayjs.utc(w.waiting_since).local().format('MMM D HH:mm')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

/**
 * Shows each capacity pool on a provider: how much of it running jobs hold, and
 * who is waiting for it, in the order they will be considered. Data comes from
 * GET /compute_provider/providers/{providerId}/pools and refreshes while open.
 */
export default function ProviderPoolsModal({
  open,
  onClose,
  providerId,
  providerName,
}: ProviderPoolsModalProps) {
  const { data, error, isLoading } = useAPI(
    'compute_provider',
    ['pools'],
    { providerId: open && providerId ? providerId : null },
    // Each refresh reads every job the team has, so keep it slow.
    { refreshInterval: 30000 },
  );

  const failed = Boolean(error) || data?.status === 'error';
  const report: ProviderPoolsReport | null =
    !failed && Array.isArray(data?.pools) ? data : null;
  const title = providerName
    ? `Capacity pools — ${providerName}`
    : 'Capacity pools';

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (failed || !report) {
    body = (
      <Alert color="danger" variant="soft">
        Failed to load capacity pools for this provider.
      </Alert>
    );
  } else if (report.pools.length === 0) {
    body = (
      <Alert color="neutral" variant="soft">
        This provider has no capacity pools, so every launch goes straight
        through. Add one from Edit.
      </Alert>
    );
  } else {
    body = (
      <Stack spacing={2.5}>
        {!report.usage_known && (
          <Alert color="warning" variant="soft">
            Could not count the GPUs in use right now, so usage is shown as
            unknown. Waiting jobs start once a full count succeeds.
          </Alert>
        )}
        {report.pools.map((pool) => (
          <PoolSection key={pool.accelerator} pool={pool} />
        ))}
        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
          Waiting jobs start oldest first. A smaller job may start ahead of a
          bigger one that does not fit yet
          {report.starvation_minutes > 0
            ? `, until the bigger one has waited ${report.starvation_minutes} min; then its pool holds for it.`
            : '. The starvation guard is off.'}
        </Typography>
      </Stack>
    );
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: 680, maxWidth: '95vw' }}>
        <ModalClose />
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>{body}</DialogContent>
      </ModalDialog>
    </Modal>
  );
}

ProviderPoolsModal.defaultProps = {
  providerName: undefined,
};
